import { useEffect, useMemo, useState } from "react";
import logo from "../picshotLogo.jpg";
import { api } from "../api";
import { EventType, PromoSettings } from "../types";
import { EventGroup } from "../components/booking/EventGroup";
import { isPortraitEvent } from "../utils/events";

type PublicCategory = "regular" | "portrait";

type PublicEventsPageProps = {
  initialCategory?: PublicCategory;
};

const fallbackPromo: PromoSettings = {
  id: "public-promo",
  enabled: true,
  label: "Special sale",
  message: "Ажлын өдрүүдэд бүх багц 20% хямдралтай",
  accentText: "PicShot promotion",
  backgroundMode: "gradient",
  startColor: "#fff0f6",
  middleColor: "#ffe9b7",
  endColor: "#ff6f9f",
  textColor: "#001f3f",
  accentColor: "#9f1239",
  speedSeconds: 25,
};

function PromoBanner({ promo }: { promo: PromoSettings }) {
  if (!promo.enabled) return null;

  const background =
    promo.backgroundMode === "solid"
      ? promo.startColor
      : `linear-gradient(90deg, ${promo.startColor} 0%, ${promo.middleColor} 45%, ${promo.endColor} 100%)`;

  return (
    <aside className="promo-ticker" aria-label="PicShot урамшуулал" style={{ background, color: promo.textColor }}>
      <a href="#booking-packages" className="promo-ticker-track" style={{ animationDuration: `${promo.speedSeconds}s` }}>
        {[0, 1, 2, 3].map((item) => (
          <span className="promo-ticker-copy" key={item}>
            <em style={{ color: promo.accentColor }}>{promo.label}</em>
            <strong>{promo.message}</strong>
            <em style={{ color: promo.accentColor }}>{promo.accentText}</em>
            <strong>{promo.message}</strong>
          </span>
        ))}
      </a>
    </aside>
  );
}

export function PublicEventsPage({ initialCategory = undefined }: PublicEventsPageProps) {
  const [events, setEvents] = useState<EventType[]>([]);
  const [promo, setPromo] = useState<PromoSettings>(fallbackPromo);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getEvents().then(setEvents).finally(() => setLoading(false));
    api.getPromo().then(setPromo).catch(() => setPromo(fallbackPromo));
  }, []);

  const regularEvents = events.filter((event) => !isPortraitEvent(event));
  const portraitEvents = events.filter((event) => isPortraitEvent(event));
  const categoryOptions = useMemo(
    () => [
      {
        id: "portrait" as const,
        href: "/portrait",
        title: "Хөрөг зураг",
        subtitle: "Биеийн дээд хэсэг гарсан зураг авалт",
        count: portraitEvents.length,
      },
      {
        id: "regular" as const,
        href: "/regular",
        title: "Энгийн зураг",
        subtitle: "Бүтэн бие орсон зураг авалт",
        count: regularEvents.length,
      },
    ],
    [portraitEvents.length, regularEvents.length]
  );

  const selectedCategory = initialCategory;

  return (
    <main className="public-page">
      <PromoBanner promo={promo} />

      <section className="public-hero">
        <img src={logo} alt="PicShot" />
        <h1>PicShot Self Portrait Studio</h1>
        <p>
          Манай студи нь өөрсдөө зургаа авдаг ба сүүлийн үеийн мэргэжлийн камер, гэрэл,
          чимэглэл хэрэглэнэ. Сонгосон багцаас хамаарч өөр өөр хэмжээтэй зураг дагалдана.
          E-Mail болон дугаараа бөглөнө үү.
        </p>
      </section>

      <section className="public-events">
        {loading && <p className="muted">Уншиж байна...</p>}

        {!loading && !selectedCategory && (
          <div className="public-category-picker" aria-label="Зураг авах төрлөө сонгох">
            {categoryOptions.map((option) => (
              <a
                className={`public-category-card ${selectedCategory === option.id ? "active" : ""}`}
                href={option.href}
                key={option.id}
              >
                <span>{option.title}</span>
                <small>{option.subtitle}</small>
                <em>{option.count} багц</em>
              </a>
            ))}
          </div>
        )}

        {!loading && !selectedCategory && (
          <p className="public-category-hint">
            Эхлээд хөрөг эсвэл энгийн зургаа сонгоно уу. Ингэснээр буруу өрөөний цаг андуурч авахгүй.
          </p>
        )}

        <div id="booking-packages" className="booking-packages-anchor" />

        {selectedCategory === "portrait" && (
          <EventGroup title="Хөрөг зургийн багц" events={portraitEvents} />
        )}
        {selectedCategory === "regular" && (
          <EventGroup title="Энгийн зургийн багц" events={regularEvents} />
        )}
      </section>
    </main>
  );
}
